import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import Calendar from "react-calendar";
import { motion } from "framer-motion";
import {
  FileText,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  Clock,
} from "lucide-react";
import "react-calendar/dist/Calendar.css";
import "./TodayMainsCalendar.css";

const loadRazorpay = (src) =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// Format date explicitly for IST
const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });

// Converts a Date to YYYY-MM-DD string (for backend)
const toYYYYMMDD = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const TodayMainsQuestion = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.token || "";
  const isPremium = user?.hasPaid;

  // ✅ GLOBAL (usable everywhere)
  const tokenHeader = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const [todayQs, setTodayQs] = useState([]);
  const [selectedQs, setSelectedQs] = useState([]);
  const [visible, setVisible] = useState({});
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  const [calendarDates, setCalendarDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const fetchTodayQuestions = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/user/mains/today`,
          tokenHeader
        );
        setTodayQs(res.data.questions || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load today’s questions");
      } finally {
        setLoading(false);
      }
    };

    const fetchAllDates = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/user/mains/all`,
          tokenHeader
        );
      const dates = (res.data.questions || []).map((q) =>
        toYYYYMMDD(new Date(q.date))
      );
        setCalendarDates([...new Set(dates)]);
      } catch (err) {
        console.error(err);
      }
    };

    fetchTodayQuestions();
    fetchAllDates();
  }, [token]);

  const toggleAnswer = (id) =>
    setVisible((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleSubmit = async (e, qid, updateFn) => {
    e.preventDefault();
    const file = e.target.file.files[0];
    if (!file) return toast.error("Please select a file");
    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type))
      return toast.error("Only PDF, JPG, or PNG allowed");

    const formData = new FormData();
    formData.append("questionId", qid);
    formData.append("file", file);

    try {
      setUploading((prev) => ({ ...prev, [qid]: true }));
      const res = await axios.post(
        `${API_BASE}/api/user/mains/submit`,
        formData,
        {
          headers: {
            ...tokenHeader.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const url = res.data.submission?.fileUrl || "";
      toast.success("Answer submitted successfully!");
      updateFn((prev) =>
        prev.map((q) =>
          q._id === qid
            ? {
                ...q,
                hasSubmitted: true,
                submittedAnswerUrl: url,
                evaluated: false,
                marksAwarded: 0,
                evaluatorComment: "",
              }
            : q
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Submission failed");
    } finally {
      setUploading((prev) => ({ ...prev, [qid]: false }));
      e.target.reset();
    }
  };

  const handleUpgrade = async () => {
  const res = await loadRazorpay(
    "https://checkout.razorpay.com/v1/checkout.js"
  );

  if (!res) {
    toast.error("Payment SDK failed to load");
    return;
  }

  try {
    const orderRes = await axios.post(
      `${API_BASE}/api/payments/create-order`,
      { type: "notesPremium" },
      { headers: { Authorization: `Bearer ${user.token}` } }
    );

    const { order } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "UPSC Journey",
      description: "Premium Access",
      order_id: order.id,

      handler: async function (response) {
        await axios.post(
          `${API_BASE}/api/payments/verify-payment`,
          {
            razorpay_order_id: order.id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            type: "notesPremium",
          },
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );

        const updatedUser = { ...user, hasPaid: true };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        toast.success("🎉 Premium Activated!");
        window.location.reload();
      },

      prefill: {
        email: user.email,
        name: user.username,
      },

      theme: { color: "#facc15" },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  } catch (err) {
    toast.error("Payment failed");
  }
};

  const handleDelete = async (qid, updateFn) => {
    if (!window.confirm("Are you sure you want to delete your submission?"))
      return;
    try {
      await axios.delete(
        `${API_BASE}/api/user/mains/delete/${qid}`,
        tokenHeader
      );
      toast.success("Submission deleted");
      updateFn((prev) =>
        prev.map((q) =>
          q._id === qid
            ? {
                ...q,
                hasSubmitted: false,
                submittedAnswerUrl: "",
                evaluated: false,
                marksAwarded: 0,
                evaluatorComment: "",
              }
            : q
        )
      );
    } catch (err) {
      toast.error("Failed to delete submission");
    }
  };

  const handleDateSelect = async (date) => {
    const dateStr = toYYYYMMDD(date);
    setSelectedDate(date);
    try {
      const res = await axios.get(
        `${API_BASE}/api/user/mains/by-date?date=${dateStr}`,
        tokenHeader
      );
      setSelectedQs(res.data.questions || []);
    } catch {
      toast.error("Failed to load questions for selected date");
      setSelectedQs([]);
    }
  };

  const tileClassName = ({ date }) =>
    calendarDates.includes(toYYYYMMDD(date))
      ? "highlighted-date"
      : "default-date";

  const renderSubmittedAnswer = (q) =>
    q.submittedAnswerUrl && (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/70 p-3"
      >
        <p className="mb-2 text-sm font-semibold text-emerald-900">
          Your Submitted Answer
        </p>
        {q.submittedAnswerUrl.toLowerCase().endsWith(".pdf") ? (
          <a
            href={q.submittedAnswerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm font-medium text-emerald-800 underline"
          >
            <FileText size={16} className="mr-1" /> View PDF
          </a>
        ) : (
          <img
            src={q.submittedAnswerUrl}
            alt="Submitted Answer"
            className="max-w-xs rounded-lg border border-emerald-200"
          />
        )}

        {q.evaluated && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-white/70 p-2 text-sm text-emerald-800">
            <p>
              <span className="font-semibold">Marks Awarded:</span>{" "}
              {q.marksAwarded}
            </p>
            <p className="mt-1">
              <span className="font-semibold">Evaluator Comment:</span>{" "}
              {q.evaluatorComment || "No comment"}
            </p>
          </div>
        )}
      </motion.div>
    );

  // polished model answer layout
  const ModelAnswerBlock = ({ answer, isPDF }) => {
    if (isPDF) {
      return (
        <a
          href={answer}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center text-sm font-medium text-indigo-700 underline"
        >
          <FileText size={16} className="mr-2" />
          View Model Answer (PDF)
        </a>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
            Model Answer
          </span>
          <span className="text-[11px] text-indigo-500">
            Structured for mains writing
          </span>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-indigo-200 via-indigo-300 to-transparent mb-3" />
        <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-line">
          {answer}
        </div>
      </motion.div>
    );
  };

  const renderQuestionCard = (q, updateFn) => {
    const show = visible[q._id];
    const isPDF = q.modelAnswer?.toLowerCase().endsWith(".pdf");

    return (
      <motion.div
        key={q._id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm shadow-slate-200/80 transition hover:-translate-y-[2px] hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Mains Practice
            </p>
            <p className="mt-1 text-base font-semibold text-slate-900">
              {q.question}
            </p>
          </div>
          <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-600">
            {formatDate(q.date)}
          </div>
        </div>

        {/* status strip */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          {q.hasSubmitted ? (
            q.evaluated ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                <CheckCircle size={14} /> Evaluated
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                <Clock size={14} /> Pending Evaluation
              </span>
            )
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              Not Submitted
            </span>
          )}

          {q.evaluated && (
            <span className="text-xs text-slate-500">
              Marks:{" "}
              <span className="font-semibold text-slate-700">
                {q.marksAwarded}
              </span>
            </span>
          )}
        </div>

        {renderSubmittedAnswer(q)}

        {!q.hasSubmitted && isPremium ? (
  <form
    onSubmit={(e) => handleSubmit(e, q._id, updateFn)}
    className="mt-4 flex flex-col gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50/80 p-3"
  >
    <label className="text-xs font-medium text-slate-700">
      Upload your written answer (PDF or image)
    </label>

    <input
      type="file"
      name="file"
      accept=".pdf,image/*"
      required
      className="block w-full cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs"
    />

    <button
      type="submit"
      disabled={uploading[q._id]}
      className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white"
    >
      {uploading[q._id] ? (
        <>
          <Loader2 className="animate-spin" size={16} /> Submitting...
        </>
      ) : (
        <>
          <Upload size={16} /> Submit Answer
        </>
      )}
    </button>
  </form>
) : !q.hasSubmitted && !isPremium ? (
  <div className="mt-4 rounded-2xl border border-yellow-300 bg-gradient-to-r from-yellow-50 to-orange-50 p-5 text-center shadow-sm">

  <p className="text-sm font-semibold text-yellow-900">
    🔒 Unlock Answer Writing Practice
  </p>

  <p className="text-xs text-yellow-700 mt-1">
    Submit answers, get evaluated, and track your improvement like toppers.
  </p>

  <button
    onClick={handleUpgrade}
    className="mt-3 inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-black rounded-lg font-semibold shadow hover:scale-105 transition"
  >
    🚀 Upgrade Now
  </button>
</div>
) : null}

        {q.hasSubmitted && (
          <button
            onClick={() => handleDelete(q._id, updateFn)}
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-700"
          >
            <Trash2 size={14} /> Delete Answer
          </button>
        )}

        {q.modelAnswer && (
          <>
            <button
              onClick={() => toggleAnswer(q._id)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
              {show ? "Hide Model Answer" : "View Model Answer"}
            </button>

            {show && <ModelAnswerBlock answer={q.modelAnswer} isPDF={isPDF} />}
          </>
        )}
      </motion.div>
    );
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        Loading...
      </div>
    );

  return (
    <div className="space-y-12 bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      {/* Today section */}
      <section>
        <h2 className="mb-2 text-2xl font-semibold text-slate-900">
          Today&apos;s Mains Questions
        </h2>
        <p className="mb-6 text-sm text-slate-600">
          Practice today&apos;s question, upload your answer sheet, and compare
          with the model answer.
        </p>
        <div className="grid gap-6 lg:grid-cols-2">
          {todayQs.length
            ? todayQs.map((q) => renderQuestionCard(q, setTodayQs))
            : "No questions available today."}
        </div>
      </section>

      {/* Previous section */}
      <section className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div>
          <h2 className="mb-2 text-2xl font-semibold text-slate-900">
            Explore Previous Questions
          </h2>
          <p className="mb-4 text-sm text-slate-600">
            Pick a date to revisit older mains questions and model answers.
          </p>
          <div className="mx-auto max-w-sm rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <Calendar
              onChange={handleDateSelect}
              value={selectedDate}
              tileClassName={tileClassName}
            />
          </div>
        </div>

        <div>
          {selectedDate && (
            <>
              <h3 className="mb-3 text-lg font-semibold text-slate-900">
                Questions for {formatDate(selectedDate)}
              </h3>
              <div className="grid gap-6">
                {selectedQs.length
                  ? selectedQs.map((q) => renderQuestionCard(q, setSelectedQs))
                  : "No questions for this date."}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default TodayMainsQuestion;
